const createNotificationSound = require('./admin-notification-sound');

document.addEventListener('DOMContentLoaded', () => {
    const feedMeta = document.querySelector('meta[name="admin-notifications-url"]');
    const feedUrl = feedMeta?.content;
    if (!feedUrl) {
        return;
    }

    const interval = 15000;
    const baseTitle = document.querySelector('title')?.dataset.adminBaseTitle || document.title;
    const favicon = document.getElementById('admin-favicon');
    const sound = createNotificationSound({
        storageKey: feedMeta.dataset.soundStorageKey,
        initialKeys: JSON.parse(document.getElementById('admin-notification-sound-snapshot')?.textContent || '[]'),
    });
    let timer = null;
    let activeRequest = null;
    let markingRead = false;
    let stopped = false;

    const countLabel = (count) => count > 99 ? '99+' : String(count);

    const render = (data) => {
        sound.update(data.sound_keys || []);
        // The tab must show the same unread count as the notification bell.
        const tabUnreadCount = data.unread_count;
        document.title = tabUnreadCount > 0 ? `🔔 (${countLabel(tabUnreadCount)}) ${baseTitle}` : baseTitle;
        if (favicon) {
            const href = tabUnreadCount > 0 ? favicon.dataset.unreadHref : favicon.dataset.defaultHref;
            if (favicon.getAttribute('href') !== href) {
                favicon.setAttribute('href', href);
            }
        }

        document.querySelectorAll('[data-admin-notification-trigger]').forEach((trigger) => {
            const unread = data.unread_count > 0;
            trigger.classList.toggle('is-unread', unread);
            trigger.setAttribute('aria-label', `Notifiche: ${data.unread_count} non lette`);
            const icon = trigger.querySelector('i');
            icon.classList.toggle('bi-bell-fill', unread);
            icon.classList.toggle('bi-bell', !unread);
            let badge = trigger.querySelector('.admin-notification-badge');
            if (unread) {
                if (!badge) {
                    badge = document.createElement('span');
                    badge.className = 'admin-notification-badge';
                    trigger.appendChild(badge);
                }
                badge.textContent = countLabel(data.unread_count);
            } else {
                badge?.remove();
            }
        });

        document.querySelectorAll('[data-admin-notification-menu]').forEach((menu) => {
            if (menu.notificationHtml === data.menu_html) {
                return;
            }

            const focused = menu.contains(document.activeElement);
            const focusedAction = focused ? document.activeElement.closest('form')?.action : null;
            const focusedHref = focused ? document.activeElement.closest('a')?.href : null;
            const scrollTop = menu.querySelector('.admin-notification-list')?.scrollTop || 0;
            // The authenticated endpoint renders the same escaped Blade partial as the page.
            menu.innerHTML = data.menu_html;
            menu.notificationHtml = data.menu_html;
            if (focused) {
                const form = Array.from(menu.querySelectorAll('form')).find((item) => item.action === focusedAction);
                const link = Array.from(menu.querySelectorAll('a')).find((item) => item.href === focusedHref);
                const target = form?.querySelector('button') || link || document.getElementById(menu.getAttribute('aria-labelledby'));
                target?.focus({ preventScroll: true });
            }
            const list = menu.querySelector('.admin-notification-list');
            if (list) {
                list.scrollTop = scrollTop;
            }
            const trigger = document.getElementById(menu.getAttribute('aria-labelledby'));
            if (trigger && menu.classList.contains('show')) {
                window.bootstrap?.Dropdown.getInstance(trigger)?.update();
            }
        });

        const counts = {
            ticket: [data.ticket_unread_count, 'notifica ticket non letta', 'notifiche ticket non lette'],
            chatbot: [data.chatbot_unread_count, 'richiesta operatore non letta', 'richieste operatore non lette'],
            communications: [data.ticket_unread_count + data.chatbot_unread_count, 'notifica comunicazioni non letta', 'notifiche comunicazioni non lette'],
        };
        document.querySelectorAll('[data-admin-notification-counter]').forEach((root) => {
            const [count, singular, plural] = counts[root.dataset.adminNotificationCounter];
            let badge = root.querySelector('.admin-notification-badge');
            let accessibleLabel = badge?.nextElementSibling;
            if (!accessibleLabel?.classList.contains('visually-hidden')) {
                accessibleLabel = null;
            }
            if (count === 0) {
                badge?.remove();
                accessibleLabel?.remove();
                return;
            }
            if (!badge) {
                badge = document.createElement('span');
                badge.className = 'admin-notification-badge admin-notification-badge-inline';
                badge.classList.toggle('admin-sidebar-group-badge', root.hasAttribute('data-admin-sidebar-group-toggle'));
                badge.setAttribute('aria-hidden', 'true');
                root.insertBefore(badge, root.querySelector('.admin-sidebar-chevron, .admin-mobile-nav-chevron'));
            }
            if (!accessibleLabel) {
                accessibleLabel = document.createElement('span');
                accessibleLabel.className = 'visually-hidden';
                badge.after(accessibleLabel);
            }
            badge.textContent = countLabel(count);
            accessibleLabel.textContent = `, ${count} ${count === 1 ? singular : plural}`;
        });
    };

    const schedule = () => {
        window.clearTimeout(timer);
        if (!stopped) {
            timer = window.setTimeout(refresh, interval);
        }
    };

    const request = async (url, form = null) => {
        window.clearTimeout(timer);
        const controller = new AbortController();
        activeRequest = controller;
        const timeout = window.setTimeout(() => controller.abort(), 10000);
        try {
            const response = await fetch(url, {
                method: form ? 'POST' : 'GET',
                credentials: 'same-origin',
                cache: 'no-store',
                headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                body: form ? new FormData(form) : undefined,
                signal: controller.signal,
            });
            if (activeRequest !== controller) {
                return;
            }
            if ([401, 403, 419].includes(response.status) || response.redirected) {
                stopped = true;
            }
            if (!response.ok || response.redirected) {
                throw new Error('Notification request failed');
            }
            const data = await response.json();
            if (activeRequest === controller) {
                render(data);
            }
        } catch (error) {
            // Keep the last successful snapshot; transient errors retry on the next poll.
            if (form) {
                const message = document.createElement('div');
                message.className = 'small text-danger px-3 py-2';
                message.setAttribute('role', 'alert');
                message.textContent = stopped
                    ? 'Sessione scaduta o accesso non disponibile. Ricarica la pagina.'
                    : 'Impossibile confermare la lettura. Riprova tra poco.';
                form.closest('[data-admin-notification-menu]')?.appendChild(message);
            }
        } finally {
            window.clearTimeout(timeout);
            if (activeRequest === controller) {
                activeRequest = null;
                schedule();
            }
        }
    };

    function refresh() {
        if (!stopped && !activeRequest && !markingRead) {
            return request(feedUrl);
        }
    }

    document.addEventListener('submit', async (event) => {
        const form = event.target.closest('[data-admin-notifications-read-all]');
        if (!form) {
            return;
        }
        event.preventDefault();
        if (markingRead || stopped) {
            return;
        }
        markingRead = true;
        // A response from a poll started before this write must not restore stale badges.
        activeRequest?.abort();
        document.querySelectorAll('[data-admin-notifications-read-all] button').forEach((button) => {
            button.disabled = true;
        });
        await request(form.action, form);
        markingRead = false;
        document.querySelectorAll('[data-admin-notifications-read-all] button').forEach((button) => {
            button.disabled = false;
        });
    });

    document.addEventListener('visibilitychange', () => {
        // Keep polling in background tabs so their title and favicon can announce arrivals.
        if (!document.hidden) {
            refresh();
        }
    });
    document.addEventListener('show.bs.dropdown', (event) => {
        if (event.target.matches('[data-admin-notification-trigger]')) {
            refresh();
        }
    });
    window.addEventListener('focus', refresh);
    window.addEventListener('online', refresh);
    window.addEventListener('pageshow', refresh);
    schedule();
});
